import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import "dayjs/locale/id";
import { FaClipboardList, FaPlus } from "react-icons/fa";
import Api from "../../api";
import DataStockCard from "../../components/stock-card/DataStockCard";
import { SipCalendar, SipDropdown, SipInput, SipSearch, SipTextarea } from "../../components/form/SipControls";

dayjs.locale("id");

const JENIS_OPTIONS = [
  { value: "keluar", label: "Distribusi ke ruangan" },
  { value: "masuk", label: "Tambah stok gudang" },
  { value: "rusak", label: "Rusak" },
  { value: "opname", label: "Opname" },
];

const emptyForm = () => ({
  barang_id: "",
  tanggal: dayjs().format("YYYY-MM-DD"),
  jenis: "keluar",
  jumlah: "",
  stok_fisik: "",
  ruangan_id: "",
  tgl_kadaluarsa: "",
  referensi: "",
  keterangan: "",
});

function StockCardPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [barangOptions, setBarangOptions] = useState([]);
  const [ruanganOptions, setRuanganOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterJenis, setFilterJenis] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Api.get("/mon/persediaan-mutasi", { params: { per_page: 200 } });
      setRows(res.data?.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Gagal memuat kartu persediaan");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOptions = useCallback(async () => {
    try {
      const [barang, ruangan] = await Promise.all([
        Api.get("/mon/persediaan/options"),
        Api.get("/mon/ruangan-filter"),
      ]);
      setBarangOptions(barang.data?.data || []);
      setRuanganOptions(ruangan.data?.ruangan || []);
    } catch (error) {
      console.error(error);
      toast.error("Gagal memuat pilihan barang atau ruangan");
    }
  }, []);

  useEffect(() => {
    loadData();
    loadOptions();
  }, [loadData, loadOptions]);

  const selected = barangOptions.find((item) => String(item.id) === String(form.barang_id));

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return rows.filter((item) => {
      const hay = [item.nama, item.kode, item.ruangan, item.keterangan, item.referensi, item.jenis]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchSearch = !q || hay.includes(q);
      const matchJenis = !filterJenis || item.jenis === filterJenis;
      return matchSearch && matchJenis;
    });
  }, [rows, searchTerm, filterJenis]);

  const counts = {
    total: filtered.length,
    keluar: filtered.filter((item) => item.jenis === "keluar").length,
    masuk: filtered.filter((item) => item.jenis === "masuk").length,
    rusak: filtered.filter((item) => item.jenis === "rusak").length,
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setErrors({});
    setIsModalOpen(true);
  };

  const handleEdit = (id) => {
    const item = rows.find((row) => String(row.id) === String(id));
    if (!item) return;
    setEditingId(item.id);
    setForm({
      barang_id: item.barang_id || "",
      tanggal: item.tanggal ? dayjs(item.tanggal).format("YYYY-MM-DD") : "",
      jenis: item.jenis || "keluar",
      jumlah: item.jenis === "opname" ? "" : String(item.jumlah ?? ""),
      stok_fisik: item.jenis === "opname" ? String(item.stok_sesudah ?? "") : "",
      ruangan_id: item.ruangan_id || "",
      tgl_kadaluarsa: item.tgl_kadaluarsa ? dayjs(item.tgl_kadaluarsa).format("YYYY-MM-DD") : "",
      referensi: item.referensi || "",
      keterangan: item.keterangan || "",
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus baris kartu ini dan hitung ulang stok barang?")) return;
    try {
      setLoading(true);
      await Api.delete(`/mon/persediaan-mutasi/${id}`);
      toast.success("Mutasi dihapus");
      await loadData();
      await loadOptions();
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal menghapus mutasi");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const next = {};
    if (!form.barang_id) next.barang_id = "Barang wajib dipilih";
    if (!form.tanggal) next.tanggal = "Tanggal wajib diisi";
    if (form.jenis === "opname") {
      if (form.stok_fisik === "" || Number(form.stok_fisik) < 0) next.stok_fisik = "Stok fisik wajib diisi";
    } else if (!form.jumlah || Number(form.jumlah) <= 0) {
      next.jumlah = "Jumlah harus lebih dari 0";
    }
    if (form.jenis === "keluar" && !form.ruangan_id) next.ruangan_id = "Ruangan tujuan wajib dipilih";
    setErrors(next);
    if (Object.keys(next).length) return;

    const payload = {
      barang_id: form.barang_id,
      tanggal: form.tanggal,
      jenis: form.jenis,
      jumlah: form.jenis === "opname" ? 0 : Number(form.jumlah),
      stok_fisik: form.jenis === "opname" ? Number(form.stok_fisik) : undefined,
      ruangan_id: form.jenis === "keluar" ? form.ruangan_id : null,
      tgl_kadaluarsa: form.jenis === "masuk" && selected?.kategori === "bahan" ? form.tgl_kadaluarsa || null : null,
      referensi: form.referensi || null,
      keterangan: form.keterangan || null,
    };

    setSaving(true);
    try {
      if (editingId) {
        await Api.put(`/mon/persediaan-mutasi/${editingId}`, payload);
        toast.success("Mutasi diperbarui");
      } else {
        await Api.post("/mon/persediaan-mutasi", payload);
        toast.success("Mutasi tersimpan");
      }
      setIsModalOpen(false);
      await loadData();
      await loadOptions();
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal menyimpan mutasi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg">
          <div className="bg-primary px-6 py-8 md:px-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="text-white">
                <div className="mb-2 flex items-center gap-3">
                  <div className="rounded-lg bg-white/20 p-2">
                    <FaClipboardList className="text-xl" />
                  </div>
                  <h1 className="text-2xl font-bold md:text-3xl">Kartu Persediaan</h1>
                </div>
                <p className="text-sm text-white/90 md:text-base">
                  Catat masuk, distribusi ke ruangan, rusak, dan opname. Master barang tetap di stelkpro.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/")}
                className="rounded-xl border border-white/20 bg-white/10 px-4 py-3 font-medium text-white hover:bg-white/20"
              >
                Kembali
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            ["Total", counts.total, "text-slate-900"],
            ["Distribusi", counts.keluar, "text-blue-600"],
            ["Masuk", counts.masuk, "text-emerald-600"],
            ["Rusak", counts.rusak, "text-rose-600"],
          ].map(([label, value, color]) => (
            <div key={label} className="rounded-xl border border-gray-100 bg-white p-5 shadow-lg">
              <p className="text-sm font-medium text-gray-600">{label}</p>
              <p className={`mt-1 text-2xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-lg sm:p-6">
          <div className="flex flex-col gap-3 tablet:flex-row tablet:items-center">
            <SipSearch
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Cari barang, kode, ruangan..."
              className="w-full min-w-0 tablet:flex-1"
            />
            <div className="grid w-full grid-cols-1 items-stretch gap-3 min-[480px]:grid-cols-[minmax(0,1fr)_auto] tablet:w-auto tablet:min-w-[22rem] tablet:shrink-0">
              <SipDropdown
                value={filterJenis}
                onChange={(event) => setFilterJenis(event.target.value)}
                placeholder="Semua jenis"
                className="h-11"
                options={[
                  { value: "", label: "Semua jenis" },
                  { value: "keluar", label: "Distribusi" },
                  { value: "masuk", label: "Masuk" },
                  { value: "rusak", label: "Rusak" },
                  { value: "opname", label: "Opname" },
                ]}
              />
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary px-4 text-sm font-medium text-white shadow-lg min-[480px]:w-auto"
              >
                <FaPlus />
                Tambah Mutasi
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg">
          <DataStockCard
            rows={filtered}
            loading={loading}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
          />
        </div>
      </div>

      <Dialog
        visible={isModalOpen}
        onHide={() => setIsModalOpen(false)}
        header={editingId ? "Edit Mutasi" : "Tambah Mutasi"}
        style={{ width: "95vw", maxWidth: "720px" }}
        modal
        draggable={false}
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Batal" className="p-button-text" onClick={() => setIsModalOpen(false)} disabled={saving} />
            <Button label={editingId ? "Update" : "Simpan"} icon="pi pi-save" onClick={handleSubmit} loading={saving} />
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">Barang</label>
            <SipDropdown
              name="barang_id"
              value={form.barang_id}
              onChange={handleChange}
              filter
              placeholder="Pilih barang"
              options={barangOptions.map((item) => ({
                value: item.id,
                label: `${item.kode} - ${item.nama} (stok ${item.stok_sekarang} ${item.satuan || ""})`,
              }))}
            />
            {errors.barang_id && <p className="mt-1 text-xs text-red-500">{errors.barang_id}</p>}
            {selected && (
              <p className="mt-1 text-xs text-gray-500">
                Stok sekarang: {selected.stok_sekarang} {selected.satuan}. Minimal: {selected.stok_minimal}. Kategori: {selected.kategori}.
              </p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Tanggal</label>
            <SipCalendar name="tanggal" value={form.tanggal} onChange={handleChange} />
            {errors.tanggal && <p className="mt-1 text-xs text-red-500">{errors.tanggal}</p>}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Jenis</label>
            <SipDropdown name="jenis" value={form.jenis} onChange={handleChange} options={JENIS_OPTIONS} />
          </div>
          {form.jenis === "opname" ? (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Stok fisik hasil hitung</label>
              <SipInput name="stok_fisik" type="number" value={form.stok_fisik} onChange={handleChange} />
              {errors.stok_fisik && <p className="mt-1 text-xs text-red-500">{errors.stok_fisik}</p>}
            </div>
          ) : (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Jumlah</label>
              <SipInput name="jumlah" type="number" value={form.jumlah} onChange={handleChange} />
              {errors.jumlah && <p className="mt-1 text-xs text-red-500">{errors.jumlah}</p>}
            </div>
          )}
          {form.jenis === "keluar" && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Ruangan tujuan</label>
              <SipDropdown
                name="ruangan_id"
                value={form.ruangan_id}
                onChange={handleChange}
                filter
                placeholder="Pilih ruangan"
                options={ruanganOptions.map((item) => ({ value: item.id, label: item.ruangan }))}
              />
              {errors.ruangan_id && <p className="mt-1 text-xs text-red-500">{errors.ruangan_id}</p>}
            </div>
          )}
          {form.jenis === "masuk" && selected?.kategori === "bahan" && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Tanggal kadaluarsa</label>
              <SipCalendar name="tgl_kadaluarsa" value={form.tgl_kadaluarsa} onChange={handleChange} />
            </div>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Referensi / nota</label>
            <SipInput name="referensi" value={form.referensi} onChange={handleChange} />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">Keterangan</label>
            <SipTextarea name="keterangan" value={form.keterangan} onChange={handleChange} rows={2} />
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default StockCardPage;
